import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { AllocationSummary as AllocSummary, ArrearsRow, AssetClass, PolicyLimitsResponse, ValuationBreach } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

function hasAny(roles: string[], ...wanted: string[]): boolean {
  return wanted.some((w) => roles.includes(w));
}

const ASSET_CLASSES: AssetClass[] = ['EQUITY', 'BOND', 'DEPOSIT', 'PROPERTY', 'LOAN'];

export function StaffInvestments() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canRead = hasAny(roles, 'SPF_INVESTMENT_OFFICER', 'SPF_PROPERTY_MANAGER', 'SPF_BOARD', 'SPF_ADMIN');
  const canInvestmentOfficer = hasAny(roles, 'SPF_INVESTMENT_OFFICER', 'SPF_ADMIN');
  const canPropertyManager = hasAny(roles, 'SPF_PROPERTY_MANAGER', 'SPF_INVESTMENT_OFFICER', 'SPF_ADMIN');

  const policyQ = useApiQuery<PolicyLimitsResponse>(canRead ? '/api/v1/investments/compliance/policy-limits' : null);
  const arrearsQ = useApiQuery<{ items: ArrearsRow[]; total: number }>(canRead ? '/api/v1/investments/properties/arrears' : null);

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Investments" subtitle="Portfolio & property (P05)" />
        <ErrorNote message="Your role does not include a P05 investments role — this page is unavailable." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Investments" subtitle="Portfolio allocation, property portfolio & development projects (P05)" />

      {policyQ.error ? <ErrorNote message={policyQ.error} /> : null}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label="Total portfolio market value" value={policyQ.loading ? '…' : policyQ.data ? scr(policyQ.data.totalMarketValue) : '—'} />
        <StatCard label="Rent arrears outstanding" value={arrearsQ.loading ? '…' : arrearsQ.data ? scr(arrearsQ.data.total) : '—'} tone={arrearsQ.data && arrearsQ.data.total > 0 ? 'warn' : 'good'} />
        <StatCard
          label="Policy breaches"
          value={policyQ.data ? policyQ.data.classes.filter((c) => !c.within).length : '—'}
          tone={policyQ.data && policyQ.data.classes.some((c) => !c.within) ? 'bad' : 'good'}
        />
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Asset allocation vs. policy limits</h2>
        <div className="card overflow-x-auto">
          {policyQ.loading ? (
            <Spinner />
          ) : (policyQ.data?.classes.length ?? 0) === 0 ? (
            <EmptyState title="No investment policies set yet" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Asset class</th>
                  <th className="table-head text-right">Actual %</th>
                  <th className="table-head text-right">Min %</th>
                  <th className="table-head text-right">Max %</th>
                  <th className="table-head">Compliance</th>
                </tr>
              </thead>
              <tbody>
                {policyQ.data!.classes.map((c) => (
                  <tr key={c.assetClass} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{c.assetClass}</td>
                    <td className="table-cell text-right tabular-nums">{c.actualPercent}%</td>
                    <td className="table-cell text-right tabular-nums">{c.minPercent}%</td>
                    <td className="table-cell text-right tabular-nums">{c.maxPercent}%</td>
                    <td className="table-cell">
                      <StatusBadge status={c.within ? 'ACTIVE' : 'WARN'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Rent arrears</h2>
        <div className="card overflow-x-auto">
          {arrearsQ.loading ? (
            <Spinner />
          ) : (arrearsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No rent arrears" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Tenant</th>
                  <th className="table-head">Unit</th>
                  <th className="table-head">Period</th>
                  <th className="table-head text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {arrearsQ.data!.items.map((row, i) => (
                  <tr key={i}>
                    <td className="table-cell">{row.tenant}</td>
                    <td className="table-cell">{row.unit}</td>
                    <td className="table-cell">{row.period}</td>
                    <td className="table-cell text-right tabular-nums">{scr(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {canInvestmentOfficer ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <SetPolicyPanel onDone={policyQ.reload} />
          <RegisterHoldingPanel />
        </div>
      ) : null}

      {canInvestmentOfficer ? (
        <div className="mt-6">
          <RecordValuationsPanel onDone={policyQ.reload} />
        </div>
      ) : null}

      {canPropertyManager ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <SignLeasePanel />
          <InvoiceRunPanel onDone={arrearsQ.reload} />
        </div>
      ) : null}

      {canPropertyManager ? (
        <div className="mt-6">
          <MarkRentPaidPanel onDone={arrearsQ.reload} />
        </div>
      ) : null}

      {canInvestmentOfficer ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <OpenProjectPanel />
          <RecordProjectSpendPanel />
        </div>
      ) : null}
    </div>
  );
}

function SetPolicyPanel({ onDone }: { onDone: () => void }) {
  const [assetClass, setAssetClass] = useState<AssetClass>('EQUITY');
  const [minPercent, setMinPercent] = useState('');
  const [maxPercent, setMaxPercent] = useState('');
  const [benchmark, setBenchmark] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/investments/policies', {
        assetClass,
        minPercent: Number.parseFloat(minPercent),
        maxPercent: Number.parseFloat(maxPercent),
        benchmark,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set policy');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Set an allocation policy</h2>
      {error ? <ErrorNote message={error} /> : null}
      <div className="space-y-3">
        <Field label="Asset class">
          <select className="field-input" value={assetClass} onChange={(e) => setAssetClass(e.target.value as AssetClass)}>
            {ASSET_CLASSES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Min %">
            <input className="field-input" type="number" step="0.1" value={minPercent} onChange={(e) => setMinPercent(e.target.value)} />
          </Field>
          <Field label="Max %">
            <input className="field-input" type="number" step="0.1" value={maxPercent} onChange={(e) => setMaxPercent(e.target.value)} />
          </Field>
        </div>
        <Field label="Benchmark">
          <input className="field-input" value={benchmark} onChange={(e) => setBenchmark(e.target.value)} placeholder="e.g. S&P 500 TR" />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !minPercent || !maxPercent || !benchmark}>
        {busy ? 'Saving…' : 'Set policy'}
      </button>
    </div>
  );
}

function RegisterHoldingPanel() {
  const [form, setForm] = useState({ class: 'BOND' as AssetClass, ref: '', cost: '', marketValue: '', valuationDate: '' });
  const [property, setProperty] = useState({ address: '', unit: '', floorArea: '' });
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const created = await api.post<{ id: string; ref: string }>('/api/v1/investments/holdings', {
        class: form.class,
        ref: form.ref,
        cost: Number.parseFloat(form.cost),
        marketValue: Number.parseFloat(form.marketValue),
        valuationDate: form.valuationDate,
        ...(form.class === 'PROPERTY'
          ? { property: { address: property.address, units: [{ unit: property.unit, floorArea: Number.parseFloat(property.floorArea) }] } }
          : {}),
      });
      setMessage(`Holding registered: ${created.ref} (id ${created.id}) — keep this ref for valuations/leases.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register holding');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Register a holding</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Asset class">
            <select className="field-input" value={form.class} onChange={(e) => setForm({ ...form, class: e.target.value as AssetClass })}>
              {ASSET_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reference">
            <input className="field-input" value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="unique holding ref" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Cost (SCR)">
            <input className="field-input" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          </Field>
          <Field label="Market value (SCR)">
            <input className="field-input" type="number" value={form.marketValue} onChange={(e) => setForm({ ...form, marketValue: e.target.value })} />
          </Field>
          <Field label="Valuation date">
            <input className="field-input" type="date" value={form.valuationDate} onChange={(e) => setForm({ ...form, valuationDate: e.target.value })} />
          </Field>
        </div>
        {form.class === 'PROPERTY' ? (
          <div className="grid grid-cols-3 gap-3 rounded-lg bg-slate-50 p-3">
            <Field label="Address" className="col-span-3">
              <input className="field-input" value={property.address} onChange={(e) => setProperty({ ...property, address: e.target.value })} />
            </Field>
            <Field label="Unit">
              <input className="field-input" value={property.unit} onChange={(e) => setProperty({ ...property, unit: e.target.value })} />
            </Field>
            <Field label="Floor area (sqm)">
              <input className="field-input" type="number" value={property.floorArea} onChange={(e) => setProperty({ ...property, floorArea: e.target.value })} />
            </Field>
          </div>
        ) : null}
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !form.ref || !form.cost || !form.marketValue || !form.valuationDate}>
        {busy ? 'Registering…' : 'Register holding'}
      </button>
    </div>
  );
}

function RecordValuationsPanel({ onDone }: { onDone: () => void }) {
  const [ref, setRef] = useState('');
  const [marketValue, setMarketValue] = useState('');
  const [valuationDate, setValuationDate] = useState('');
  const [breaches, setBreaches] = useState<ValuationBreach[] | null>(null);
  const [allocation, setAllocation] = useState<AllocSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setBreaches(null);
    try {
      const r = await api.post<{ applied: number; allocation: AllocSummary; breaches: ValuationBreach[] }>('/api/v1/investments/valuations', {
        valuations: [{ ref, marketValue: Number.parseFloat(marketValue), valuationDate }],
      });
      setBreaches(r.breaches);
      setAllocation(r.allocation);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record valuation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Record a quarterly valuation</h2>
      <p className="mb-3 text-xs text-slate-500">Enter the holding's existing reference — a policy-limit breach auto-alerts the board.</p>
      {error ? <ErrorNote message={error} /> : null}
      <div className="grid grid-cols-3 gap-3">
        <Field label="Holding reference">
          <input className="field-input" value={ref} onChange={(e) => setRef(e.target.value)} />
        </Field>
        <Field label="New market value (SCR)">
          <input className="field-input" type="number" value={marketValue} onChange={(e) => setMarketValue(e.target.value)} />
        </Field>
        <Field label="Valuation date">
          <input className="field-input" type="date" value={valuationDate} onChange={(e) => setValuationDate(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !ref || !marketValue || !valuationDate}>
        {busy ? 'Applying…' : 'Apply valuation'}
      </button>
      {breaches ? (
        <div className="mt-3">
          {breaches.length === 0 ? (
            <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
              Applied. Total portfolio: {allocation ? scr(allocation.total) : '—'}. No policy breaches.
            </div>
          ) : (
            <div className="rounded-lg bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
              Policy breach — board alert emitted: {breaches.map((b) => `${b.assetClass} ${b.actualPercent}%`).join(', ')}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SignLeasePanel() {
  const [holdingRef, setHoldingRef] = useState('');
  const [unit, setUnit] = useState('');
  const [tenant, setTenant] = useState('');
  const [rent, setRent] = useState('');
  const [escalationPercent, setEscalationPercent] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/api/v1/investments/properties/${holdingRef}/leases`, {
        unit,
        tenant,
        rent: Number.parseFloat(rent),
        escalationPercent: Number.parseFloat(escalationPercent),
      });
      setMessage('Lease signed.');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to sign lease');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Sign a lease</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="space-y-3">
        <Field label="Property holding reference">
          <input className="field-input" value={holdingRef} onChange={(e) => setHoldingRef(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Unit">
            <input className="field-input" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Field>
          <Field label="Tenant">
            <input className="field-input" value={tenant} onChange={(e) => setTenant(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monthly rent (SCR)">
            <input className="field-input" type="number" value={rent} onChange={(e) => setRent(e.target.value)} />
          </Field>
          <Field label="Annual escalation %">
            <input className="field-input" type="number" step="0.1" value={escalationPercent} onChange={(e) => setEscalationPercent(e.target.value)} />
          </Field>
        </div>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !holdingRef || !unit || !tenant || !rent}>
        {busy ? 'Signing…' : 'Sign lease'}
      </button>
    </div>
  );
}

function InvoiceRunPanel({ onDone }: { onDone: () => void }) {
  const [period, setPeriod] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await api.post<{ period: string; issued: number }>(`/api/v1/investments/leases/invoice-run/${period}`);
      setMessage(`${r.issued} rent invoice(s) issued for ${r.period}.`);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Invoice run failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Run monthly rent invoicing</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <Field label="Period (YYYY-MM)">
        <input className="field-input max-w-[160px]" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07" />
      </Field>
      <button className="btn-secondary mt-3" onClick={submit} disabled={busy || !period}>
        {busy ? 'Running…' : 'Issue invoices'}
      </button>
    </div>
  );
}

function MarkRentPaidPanel({ onDone }: { onDone: () => void }) {
  const [invoiceId, setInvoiceId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/api/v1/investments/rent-invoices/${invoiceId}/paid`);
      setMessage('Rent invoice marked paid.');
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to mark invoice paid');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Mark a rent invoice paid</h2>
      <p className="mb-3 text-xs text-slate-500">
        The arrears list above doesn't expose invoice IDs (upstream API limitation) — enter the ID from your bank
        reconciliation.
      </p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <Field label="Rent invoice ID">
        <input className="field-input max-w-xs" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} />
      </Field>
      <button className="btn-secondary mt-3" onClick={submit} disabled={busy || !invoiceId}>
        {busy ? 'Marking…' : 'Mark paid'}
      </button>
    </div>
  );
}

function OpenProjectPanel() {
  const [name, setName] = useState('');
  const [budget, setBudget] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const p = await api.post<{ id: string; name: string }>('/api/v1/investments/projects', { name, budget: Number.parseFloat(budget) });
      setMessage(`Project opened: ${p.name} (id ${p.id}) — keep this id to record spend.`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open project');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Open a development project</h2>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Project name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Approved budget (SCR)">
          <input className="field-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !name || !budget}>
        {busy ? 'Opening…' : 'Open project'}
      </button>
    </div>
  );
}

function RecordProjectSpendPanel() {
  const [projectId, setProjectId] = useState('');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.post(`/api/v1/investments/projects/${projectId}/spend`, { amount: Number.parseFloat(amount) });
      setMessage('Spend recorded.');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record spend (over-budget spend is rejected)');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Record project spend</h2>
      <p className="mb-3 text-xs text-slate-500">There is no project list endpoint upstream — use the ID shown when you opened the project.</p>
      {error ? <ErrorNote message={error} /> : null}
      {message ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{message}</div> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Project ID">
          <input className="field-input" value={projectId} onChange={(e) => setProjectId(e.target.value)} />
        </Field>
        <Field label="Spend amount (SCR)">
          <input className="field-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !projectId || !amount}>
        {busy ? 'Recording…' : 'Record spend'}
      </button>
    </div>
  );
}
